import type {
  AddCompanyRequest,
  AddCompanyResponse,
  CompanyDetailsResponse,
  CompanySearchRequest,
  CompanySearchResponse,
  DeleteCompanyResponse,
  GetAllCompaniesResponse,
  GetCompanyByIdResponse,
  UpdateCompanyRequest,
  UpdateCompanyResponse,
} from '../types'
import { api } from './api'

class CompanyService {
  /**
   * Search for companies using the Companies House API
   * @param searchParams - Search parameters including query string and pagination
   * @returns Promise with search results
   */
  async searchCompanies(
    searchParams: CompanySearchRequest
  ): Promise<CompanySearchResponse> {
    const queryParams = new URLSearchParams()
    queryParams.append('q', searchParams.q)

    if (searchParams.items_per_page) {
      queryParams.append(
        'items_per_page',
        searchParams.items_per_page.toString()
      )
    }

    if (searchParams.start_index) {
      queryParams.append('start_index', searchParams.start_index.toString())
    }

    return await api.get<CompanySearchResponse['response']>(
      `/company/search-companies-house?${queryParams.toString()}`
    )
  }

  /**
   * Get detailed information about a specific company
   * @param companyNumber - The company registration number
   * @returns Promise with company details
   */
  async getCompanyDetails(
    companyNumber: string
  ): Promise<CompanyDetailsResponse> {
    return await api.get<CompanyDetailsResponse['response']>(
      `/company/get-company-details-from-companies-house/${companyNumber}`
    )
  }

  /**
   * Get live Confirmation Statement details (statement date, filing deadline,
   * overdue status, registered office, SIC codes, directors and PSC) straight
   * from Companies House. Used by the Confirmation Statement filing flow.
   */
  async getConfirmationStatementDetails(companyNumber: string): Promise<{ response: any }> {
    return await api.get<any>(`/company/confirmation-statement-details/${companyNumber}`)
  }

  /**
   * Search companies with default pagination settings
   * @param query - Search query string
   * @returns Promise with search results (10 items per page by default)
   */
  async searchCompaniesSimple(query: string): Promise<CompanySearchResponse> {
    return this.searchCompanies({
      q: query,
      items_per_page: 10,
    })
  }

  /**
   * Add a company from Companies House data
   * @param companyData - Company data to add
   * @returns Promise with the added company data
   */
  async addCompanyFromCompaniesHouse(
    companyData: AddCompanyRequest
  ): Promise<AddCompanyResponse> {
    return await api.post<AddCompanyResponse['response']>(
      '/company/add-from-companies-house',
      companyData
    )
  }

  /**
   * Get all companies with pagination
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @returns Promise with paginated company list
   */
  async getAllCompanies(
    page: number = 1,
    limit: number = 10
  ): Promise<GetAllCompaniesResponse> {
    const queryParams = new URLSearchParams()
    queryParams.append('page', page.toString())
    queryParams.append('limit', limit.toString())

    return await api.get<GetAllCompaniesResponse['response']>(
      `/company/get-all-companies?${queryParams.toString()}`
    )
  }

  /**
   * Get a specific company by ID
   * @param companyId - The company's database ID
   * @returns Promise with company details
   */
  async getCompanyById(companyId: string): Promise<GetCompanyByIdResponse> {
    return await api.get<GetCompanyByIdResponse['response']>(
      `/company/get-company-by-id/${companyId}`
    )
  }

  /**
   * Update a company's information
   * @param companyId - The company's database ID
   * @param updateData - Data to update
   * @returns Promise with updated company data
   */
  async updateCompany(
    companyId: string,
    updateData: UpdateCompanyRequest
  ): Promise<UpdateCompanyResponse> {
    return await api.put<UpdateCompanyResponse['response']>(
      `/company/update-company/${companyId}`,
      updateData
    )
  }

  /**
   * Delete a company
   * @param companyId - The company's database ID
   * @returns Promise with deletion confirmation
   */
  async deleteCompany(companyId: string): Promise<DeleteCompanyResponse> {
    return await api.delete<DeleteCompanyResponse['response']>(
      `/company/delete-company/${companyId}`
    )
  }
}

export const companyService = new CompanyService()
export default companyService
